/**
 * Pure helper behind the Economics strip's sleep/wake countdown.
 *
 * The sales agent runs on a daily LLM budget anchored to a UTC reset hour
 * (default noon). When the cap is hit it "sleeps" — skips all LLM work until
 * the next reset. The operator wanted to SEE that: while awake, roughly how
 * long until it sleeps (projected from the current burn rate); while asleep,
 * how long until it wakes (exact, from the agent's `resets_at`).
 *
 * Kept pure (no Date.now / no React) so the cases are unit-tested deterministically;
 * the component passes a ticking `now` and re-renders.
 */
export interface BudgetCountdownInput {
  /** Current instant, epoch ms (the component passes a ticking value). */
  now: number;
  /** Next reset / wake instant, epoch ms — from the agent's `resets_at`.
   * null when the agent is too old to report it. */
  resetsAtMs: number | null;
  /** Current budget-window start, epoch ms — from the agent's `window_start`. */
  windowStartMs: number | null;
  spendUsd: number;
  budgetUsd: number;
  overBudget: boolean;
}

export interface BudgetCountdown {
  state: "sleeping" | "active" | "unknown";
  label: string;
}

/** "3h 0m" / "12m" / "0m" — never negative. */
function fmtDur(ms: number): string {
  const totalMin = Math.max(0, Math.floor(ms / 60_000));
  const h = Math.floor(totalMin / 60);
  const m = totalMin % 60;
  return h > 0 ? `${h}h ${m}m` : `${m}m`;
}

export function budgetCountdown(input: BudgetCountdownInput): BudgetCountdown {
  const { now, resetsAtMs, windowStartMs, spendUsd, budgetUsd, overBudget } = input;

  // No reset timestamp → the agent predates this field; show nothing rather
  // than guess.
  if (resetsAtMs == null) {
    return { state: "unknown", label: "" };
  }

  const wakeMs = Math.max(0, resetsAtMs - now);

  if (overBudget || (budgetUsd > 0 && spendUsd >= budgetUsd)) {
    return { state: "sleeping", label: `Sleeping · wakes in ${fmtDur(wakeMs)}` };
  }

  // Active: project time-to-sleep from the burn rate so far this window.
  const elapsedMs = windowStartMs != null ? now - windowStartMs : 0;
  const remainingUsd = budgetUsd - spendUsd;
  if (elapsedMs > 0 && spendUsd > 0 && budgetUsd > 0 && remainingUsd > 0) {
    const burnPerMs = spendUsd / elapsedMs;
    const sleepInMs = remainingUsd / burnPerMs;
    if (sleepInMs < wakeMs) {
      return {
        state: "active",
        label: `~Sleeps in ${fmtDur(sleepInMs)} · resets ${fmtDur(wakeMs)}`,
      };
    }
    return {
      state: "active",
      label: `On pace through the day · resets in ${fmtDur(wakeMs)}`,
    };
  }

  // No spend yet (or no window start) → just the reset countdown.
  return { state: "active", label: `Budget resets in ${fmtDur(wakeMs)}` };
}
